/**
 * ConstitutionPanel — J2-A
 *
 * Displays and manages the org's Constitution entries (system-scoped principles
 * with is_constitution_entry = true). Founders can add, edit, and delete entries.
 *
 * Gated on roros.governance.manage.
 * Embedded in SystemSettingsPage as the "Constitution" tab.
 *
 * PRD §12.3.5 task 1 + §9.12.1.
 *
 * Wave 6 redesign (R1.b): Add and Edit Modals promoted to FormSheets.
 * Each Sheet gives the description textarea generous vertical space
 * (min-h-[280px]) so authors can write constitution entries as paragraphs
 * of intent rather than squeezing into a cramped modal.
 */

import React, { useEffect, useState } from 'react';
import { Landmark, Plus, Pencil, Trash2 } from 'lucide-react';
import { useFormSheetUrlRouting } from '@/hooks/useFormSheetUrlRouting';
import { t } from '@/lib/i18n';
import { usePermissions } from '@/hooks/usePermissions';
import { PERMISSIONS } from '@/constants/permissions';
import { useFormSnapshot } from '@/hooks/useFormSnapshot';
import { FormSheet } from '@/components/ui/FormSheet';
import { QueryPage } from '@/components/QueryPage';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog/ConfirmDialog';
import { Alert } from '@/components/ui/Alert';
import { showToast } from '@/components/ui/Toast';
import {
  useRorosConstitution,
  useRorosConstitutionAdd,
  useRorosConstitutionUpdate,
  useRorosConstitutionDelete,
} from '@/hooks/roros/governance';
import type { RorosConstitutionEntryDTO } from '@/services/roros/governance.service';

// ─── Snapshot type & diff ─────────────────────────────────────────────────────

type ConstitutionEntrySnapshot = {
  title: string;
  description: string;
};

function diffConstitutionEntry(
  base: ConstitutionEntrySnapshot,
  cur: ConstitutionEntrySnapshot,
): Partial<Record<keyof ConstitutionEntrySnapshot, boolean>> {
  return {
    title: base.title !== cur.title,
    description: base.description !== cur.description,
  };
}

// ─── Entry card ────────────────────────────────────────────────────────────

interface EntryCardProps {
  entry: RorosConstitutionEntryDTO;
  canManage: boolean;
  onEdit: (entry: RorosConstitutionEntryDTO) => void;
  onDelete: (entry: RorosConstitutionEntryDTO) => void;
  index: number;
}

function EntryCard({ entry, canManage, onEdit, onDelete, index }: EntryCardProps) {
  return (
    <div
      role="listitem"
      className="rounded-lg border border-border bg-card p-4 flex items-start gap-3"
    >
      <span
        aria-hidden
        className="flex-shrink-0 h-6 w-6 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-semibold mt-0.5"
      >
        {index + 1}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground leading-snug">{entry.title}</p>
        {entry.description && (
          <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{entry.description}</p>
        )}
      </div>
      {canManage && (
        <div className="flex-shrink-0 flex items-center gap-1 ml-2">
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('Edit')}
            onClick={() => onEdit(entry)}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('Delete')}
            onClick={() => onDelete(entry)}
            className="text-error-600 hover:text-error-700 hover:bg-error-50"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Main panel ────────────────────────────────────────────────────────────────

/** Stable getId reference for useFormSheetUrlRouting (defined outside component). */
const getConstitutionEntryId = (e: RorosConstitutionEntryDTO) => e.id;

export function ConstitutionPanel() {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(PERMISSIONS.ROROS_GOVERNANCE_MANAGE);

  const query = useRorosConstitution();
  const addMutation = useRorosConstitutionAdd();
  const updateMutation = useRorosConstitutionUpdate();
  const deleteMutation = useRorosConstitutionDelete();

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<RorosConstitutionEntryDTO | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // ─── AC-F27-02: Deep-link READ — open edit sheet when ?edit-constitution=<id> ──
  // getId defined stable at module level (see constant below component) per hook contract.
  const { deepLinkTarget: deepLinkedEntry, consumeDeepLink } = useFormSheetUrlRouting({
    urlKey: 'edit-constitution',
    items: query.data,
    getId: getConstitutionEntryId,
  });

  useEffect(() => {
    if (!deepLinkedEntry) return;
    setEditingEntry(deepLinkedEntry);
    setFormError(null);
    consumeDeepLink();
  }, [deepLinkedEntry, consumeDeepLink]);

  // ─── Add form state ────────────────────────────────────────────────────────

  const {
    state: addState,
    setField: setAddField,
    baseline: addBaseline,
    current: addCurrent,
  } = useFormSnapshot<ConstitutionEntrySnapshot>({
    initialFactory: () => ({ title: '', description: '' }),
    isOpen: isAddOpen,
  });

  // ─── Edit form state ───────────────────────────────────────────────────────

  const {
    state: editState,
    setField: setEditField,
    baseline: editBaseline,
    current: editCurrent,
  } = useFormSnapshot<ConstitutionEntrySnapshot>({
    initialFactory: () =>
      editingEntry
        ? { title: editingEntry.title, description: editingEntry.description ?? '' }
        : { title: '', description: '' },
    isOpen: !!editingEntry,
    depKey: editingEntry?.id ?? null,
  });

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleAdd = () => {
    if (!addState.title.trim()) {
      setFormError(t('Title is required'));
      return;
    }
    setFormError(null);
    addMutation.mutate(
      { title: addState.title.trim(), description: addState.description.trim() },
      {
        onSuccess: () => {
          setIsAddOpen(false);
          showToast.success(t('Constitution entry added'));
        },
        onError: (err: any) => {
          setFormError(err?.message ?? t('Failed to add entry'));
        },
      },
    );
  };

  const handleUpdate = () => {
    if (!editingEntry) return;
    if (!editState.title.trim()) {
      setFormError(t('Title is required'));
      return;
    }
    setFormError(null);
    updateMutation.mutate(
      { id: editingEntry.id, payload: { title: editState.title.trim(), description: editState.description.trim() } },
      {
        onSuccess: () => {
          setEditingEntry(null);
          showToast.success(t('Constitution entry updated'));
        },
        onError: (err: any) => {
          setFormError(err?.message ?? t('Failed to update entry'));
        },
      },
    );
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: () => {
        setDeleteTarget(null);
        showToast.success(t('Constitution entry deleted'));
      },
      onError: (err: any) => {
        showToast.error(err?.message ?? t('Failed to delete entry'));
      },
    });
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-2xl space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
            <Landmark className="h-5 w-5 text-primary-600" aria-hidden />
            {t('Governance Principles')}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('Operating principles of the ROROS framework — how the system itself thinks and works.')}
          </p>
        </div>
        {canManage && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => { setIsAddOpen(true); setFormError(null); }}
            className="flex-shrink-0"
          >
            <Plus className="h-4 w-4 mr-1" aria-hidden />
            {t('Add Entry')}
          </Button>
        )}
      </div>

      {/* Constitution list */}
      <QueryPage
        query={query}
        emptyMessage={t('No Constitution entries yet. Add your first operating principle.')}
        emptyIcon={<Landmark className="h-8 w-8" />}
        emptyAction={
          canManage ? (
            <Button size="sm" onClick={() => { setIsAddOpen(true); setFormError(null); }}>
              <Plus className="h-4 w-4 mr-1" />
              {t('Add Entry')}
            </Button>
          ) : undefined
        }
      >
        {(entries: RorosConstitutionEntryDTO[]) => (
          <div role="list" className="space-y-2">
            {entries
              .map((entry, index) => (
                <EntryCard
                  key={entry.id}
                  entry={entry}
                  canManage={canManage}
                  onEdit={(e) => { setEditingEntry(e); setFormError(null); }}
                  onDelete={(e) => setDeleteTarget({ id: e.id, title: e.title })}
                  index={index}
                />
              ))}
          </div>
        )}
      </QueryPage>

      {/* ── Add Entry Sheet ─────────────────────────────────────────────────── */}
      {/*
        Wave 6 redesign: promoted from Modal to FormSheet (R1.b).
        Description textarea gets generous height (min-h-[280px]) so authors
        can write constitution entries as multi-paragraph statements of intent.
        Helper text below each field frames the writing task.
      */}
      <FormSheet<ConstitutionEntrySnapshot>
        isOpen={isAddOpen}
        onClose={() => { setIsAddOpen(false); setFormError(null); }}
        title={t('Add Constitution Entry')}
        mode="create"
        baseline={addBaseline}
        current={addCurrent}
        diff={diffConstitutionEntry}
        onSubmit={handleAdd}
        isSubmitting={addMutation.isPending}
        submitLabel={t('Add')}
        submittingLabel={t('Adding…')}
        submitTestId="constitution-add-save"
        width="md"
      >
        <div className="space-y-6">
          {formError && isAddOpen && (
            <Alert variant="error" data-testid="constitution-add-error">
              {formError}
            </Alert>
          )}

          {/* Title */}
          <div>
            <label
              className="block text-sm font-medium text-foreground mb-1"
              htmlFor="constitution-add-title"
            >
              {t('Title')} <span className="text-error-500" aria-hidden>*</span>
            </label>
            <Input
              id="constitution-add-title"
              value={addState.title}
              onChange={(e) => {
                setAddField('title', e.target.value);
                if (formError) setFormError(null);
              }}
              placeholder={t('e.g. Processes are the problem, not people')}
              required
              maxLength={255}
              autoFocus
              data-testid="constitution-title-input"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t('Short, memorable name for the principle')}
            </p>
          </div>

          {/* Description / Body */}
          <div>
            <label
              className="block text-sm font-medium text-foreground mb-1"
              htmlFor="constitution-add-description"
            >
              {t('Body')}
            </label>
            <Textarea
              id="constitution-add-description"
              value={addState.description}
              onChange={(e) => setAddField('description', e.target.value)}
              placeholder={t('Explain this principle in 2–3 sentences')}
              className="min-h-[280px] resize-y"
              data-testid="constitution-description-input"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t('What does this principle mean in practice? Why does it matter to the team?')}
            </p>
          </div>
        </div>
      </FormSheet>

      {/* ── Edit Entry Sheet ────────────────────────────────────────────────── */}
      {/*
        Wave 6 redesign: promoted from Modal to FormSheet (R1.b).
        Same generous layout as Add. URL-routed via ?edit-constitution=<id>
        for deep-link consistency with Goals, Outcomes, and other artifact pages.
      */}
      <FormSheet<ConstitutionEntrySnapshot>
        isOpen={!!editingEntry}
        onClose={() => { setEditingEntry(null); setFormError(null); }}
        title={t('Edit Constitution Entry')}
        mode="edit"
        baseline={editBaseline}
        current={editCurrent}
        diff={diffConstitutionEntry}
        onSubmit={handleUpdate}
        isSubmitting={updateMutation.isPending}
        submitLabel={t('Save Changes')}
        submittingLabel={t('Saving…')}
        submitTestId="constitution-edit-save"
        width="md"
        urlKey="edit-constitution"
        urlId={editingEntry?.id}
      >
        <div className="space-y-6">
          {formError && !!editingEntry && (
            <Alert variant="error" data-testid="constitution-edit-error">
              {formError}
            </Alert>
          )}

          {/* Title */}
          <div>
            <label
              className="block text-sm font-medium text-foreground mb-1"
              htmlFor="constitution-edit-title"
            >
              {t('Title')} <span className="text-error-500" aria-hidden>*</span>
            </label>
            <Input
              id="constitution-edit-title"
              value={editState.title}
              onChange={(e) => {
                setEditField('title', e.target.value);
                if (formError) setFormError(null);
              }}
              placeholder={t('e.g. Processes are the problem, not people')}
              required
              maxLength={255}
              autoFocus
              data-testid="constitution-edit-title-input"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t('Short, memorable name for the principle')}
            </p>
          </div>

          {/* Description / Body */}
          <div>
            <label
              className="block text-sm font-medium text-foreground mb-1"
              htmlFor="constitution-edit-description"
            >
              {t('Body')}
            </label>
            <Textarea
              id="constitution-edit-description"
              value={editState.description}
              onChange={(e) => setEditField('description', e.target.value)}
              placeholder={t('Explain this principle in 2–3 sentences')}
              className="min-h-[280px] resize-y"
              data-testid="constitution-edit-description-input"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t('What does this principle mean in practice? Why does it matter to the team?')}
            </p>
          </div>
        </div>
      </FormSheet>

      {/* Delete confirm */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title={t('Delete Constitution Entry')}
        description={deleteTarget ? `${t('Are you sure you want to delete')} "${deleteTarget.title}"? ${t('This cannot be undone.')}` : ''}
        confirmText={t('Delete')}
        variant="destructive"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
